import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SEOHead } from '@/components/layout/SEOHead';
import { FadeIn } from '@/components/motion/FadeIn';
import { getJournalPost, getAllJournalPosts } from '@/lib/content';
import { buildArticleSchema, buildBreadcrumbSchema, buildSchemaGraph } from '@/lib/seo';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
}

/** Count words in an element's text content. */
function countWords(el: HTMLElement | null): number {
  if (!el) return 0;
  return (el.textContent ?? '').trim().split(/\s+/).filter(Boolean).length;
}

export function JournalPostPage() {
  const { slug } = useParams();
  const post = slug ? getJournalPost(slug) : null;
  const all = getAllJournalPosts();
  const bodyRef = useRef<HTMLDivElement>(null);
  const [readMinutes, setReadMinutes] = useState<number | null>(null);

  useEffect(() => {
    if (!bodyRef.current) return;
    const words = countWords(bodyRef.current);
    setReadMinutes(Math.max(1, Math.round(words / 225)));
  }, [post?.slug]);

  if (!post) {
    return (
      <>
        <SEOHead title="Post not found · Brian Ellison Design" />
        <section className="container section">
          <p className="eyebrow">404</p>
          <h1>This entry couldn't be found.</h1>
          <p><Link to="/journal" className="text-link">Return to the journal</Link></p>
        </section>
      </>
    );
  }

  const idx = all.findIndex((p) => p.slug === post.slug);
  const next = all[(idx + 1) % all.length];
  const schema = buildSchemaGraph(
    buildArticleSchema(post),
    buildBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Journal', path: '/journal' },
      { name: post.title, path: `/journal/${post.slug}` },
    ]),
  );
  const Body = post.Body;

  return (
    <>
      <SEOHead
        title={`${post.title} · Journal · Brian Ellison Design`}
        description={post.excerpt}
        canonical={`https://brianellisondesign.com/journal/${post.slug}`}
        ogImage="/posters/og-journal.svg"
        schema={schema}
      />

      <article className="journal-post">
        <header className="journal-post__hero page-hero">
          <div className="container journal-post__hero-inner">
            <FadeIn>
              <p className="eyebrow journal-post__meta">
                {formatDate(post.date)}
                {post.category ? ` · ${post.category}` : ''}
                {readMinutes !== null ? ` · ${readMinutes} min read` : ''}
              </p>
              <h1 className="page-hero__title">{post.title}</h1>
              <p className="subtitle">{post.excerpt}</p>
            </FadeIn>
          </div>
        </header>

        <div ref={bodyRef} className="container journal-post__body drop-cap">
          <Body />
        </div>

        <p className="ornament container" aria-hidden="true">§</p>

        <div className="container journal-post__author">
          <p className="caption">— {post.author}, BED studio.</p>
        </div>

        {next ? (
          <section className="project-next container section">
            <p className="eyebrow">Next in the journal</p>
            <Link to={`/journal/${next.slug}`} className="project-next__link">
              <span className="project-next__chevron" aria-hidden="true">→</span>
              <span className="display-italic project-next__title">{next.title}</span>
            </Link>
          </section>
        ) : null}
      </article>
    </>
  );
}
